import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Flower2, Flower } from "lucide-react";
import Button from "../../../components/common/Button";
import Card from "../../../components/common/Card";
import ErrorState from "../../../components/common/ErrorState";
import { CardGridSkeleton } from "../../../components/common/Skeleton";
import { formatDate } from "../../../utils/formatters";

// 향년(만 나이) 계산
const calcAge = (birth, death) => {
  if (!birth || !death) return null;
  const b = new Date(birth);
  const d = new Date(death);
  if (Number.isNaN(b.getTime()) || Number.isNaN(d.getTime())) return null;
  let age = d.getFullYear() - b.getFullYear();
  const m = d.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && d.getDate() < b.getDate())) age -= 1;
  return age;
};

// 발인 상태 판정 (오늘 기준): 발인일이 지났으면 발인완료, 아니면 조문중
const getDepartureStatus = (departureDate) => {
  if (!departureDate) return { label: "조문중", className: "bg-green-50 text-green-700" };
  const dep = new Date(`${departureDate}T23:59:59`);
  const now = new Date();
  if (Number.isNaN(dep.getTime())) {
    return { label: "조문중", className: "bg-green-50 text-green-700" };
  }
  return dep.getTime() < now.getTime()
    ? { label: "발인완료", className: "bg-gray-100 text-gray-500" }
    : { label: "조문중", className: "bg-green-50 text-green-700" };
};

const RecentObituariesSection = () => {
  const navigate = useNavigate();
  const [obituaries, setObituaries] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | error | success

  const loadObituaries = useCallback(async () => {
    setStatus("loading");
    try {
      const response = await fetch("/mockdata/obituaries.json");
      if (!response.ok) throw new Error("Failed to fetch obituaries");
      const data = await response.json();
      const sorted = [...data].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setObituaries(sorted.slice(0, 4));
      setStatus("success");
    } catch (error) {
      console.error("부고 데이터를 불러오는데 실패했습니다:", error);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    loadObituaries();
  }, [loadObituaries]);

  const goToDetail = (id) => navigate(`/obituaries/${id}`);

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold text-charcoal">최근 부고</h2>
          </div>
          <Link to="/obituaries">
            <Button variant="outline" size="sm">
              전체보기 <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>

        {status === "loading" && (
          <CardGridSkeleton count={4} columns={4} withImage={false} />
        )}

        {status === "error" && (
          <ErrorState
            title="최근 부고를 불러오지 못했습니다"
            message="네트워크 상태를 확인한 뒤 다시 시도해주세요."
            onRetry={loadObituaries}
          />
        )}

        {status === "success" && obituaries.length === 0 && (
          <Card className="bg-white border border-gray-200">
            <div className="p-8 text-center text-gray-500">
              등록된 부고가 없습니다.
            </div>
          </Card>
        )}

        {status === "success" && obituaries.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {obituaries.map((obituary) => {
              const age = calcAge(
                obituary.deceasedBirthDate,
                obituary.deceasedDeathDate
              );
              const dep = getDepartureStatus(obituary.departureDate);
              return (
                <Card
                  key={obituary.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`故 ${obituary.deceasedName} 부고 상세 보기`}
                  onClick={() => goToDetail(obituary.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      goToDetail(obituary.id);
                    }
                  }}
                  className="h-full hover:shadow-xl transition-all cursor-pointer bg-white border border-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <div className="p-6">
                    <div className="mb-3 flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50">
                          <Flower size={18} className="text-primary-700" aria-hidden="true" />
                        </span>
                        <h3 className="text-lg font-bold text-charcoal">
                          故 {obituary.deceasedName}
                        </h3>
                      </div>
                      <span
                        className={`shrink-0 text-xs px-2 py-1 rounded font-medium ${dep.className}`}
                      >
                        {dep.label}
                      </span>
                    </div>
                    {age != null && (
                      <p className="text-gray-600 mb-4 text-sm">향년 {age}세</p>
                    )}
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-start gap-2">
                        <MapPin
                          size={16}
                          className="mt-0.5 flex-shrink-0 text-charcoal"
                          aria-hidden="true"
                        />
                        <div>
                          <p className="font-medium text-charcoal">
                            {obituary.funeralHome}
                          </p>
                          {obituary.funeralRoom && (
                            <p className="text-gray-500">{obituary.funeralRoom}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-charcoal" aria-hidden="true" />
                        <span>
                          발인 {formatDate(obituary.departureDate, "yyyy.MM.dd")}
                          {obituary.departureTime ? ` ${obituary.departureTime}` : ""}
                        </span>
                      </div>
                    </div>

                    {/* 빠른 액션 */}
                    <div className="mt-4 flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToDetail(obituary.id);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-primary-900 px-3 py-1.5 text-xs font-medium text-primary-900 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        조문하기
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/wreaths");
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-primary-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        <Flower2 size={14} aria-hidden="true" />
                        화환
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default RecentObituariesSection;
